import type { QrDoLote } from '../../api/lotes.js';
import { Botao } from '../../componentes/Botao.js';
import { DesenhoDaEtiquetaDeLote } from './DesenhoDaEtiquetaDeLote.js';
import type { TamanhoDeEtiqueta } from './etiqueta.js';
import type { LoteDaEtiqueta } from './etiqueta-lote.js';
import type { EstadoDoQrDe } from './useQrPorCodigo.js';
import './PreviaDasEtiquetas.css';

/** Recusa que não muda na segunda tentativa: o texto não cabe no QR. */
export const LOTE_NAO_CABE_NO_QR = 'LOTE_NAO_CABE_NO_QR';

type Props = {
  readonly lotes: readonly LoteDaEtiqueta[];
  /** Estado do QR por `codLot`: dois produtos com o mesmo lote têm a mesma matriz. */
  readonly estados: ReadonlyMap<string, EstadoDoQrDe<QrDoLote>>;
  readonly tamanho: TamanhoDeEtiqueta;
  readonly aoRemover: (chave: string) => void;
  readonly aoTentarDeNovo: (codLot: string) => void;
};

/**
 * Um cartão por lote, na ordem da lista, com a etiqueta como vai sair. Cada
 * cartão conta o próprio desfecho, como na etiqueta de produto; lote que não
 * cabe no QR não oferece tentar de novo.
 */
export function PreviaDasEtiquetasDeLote({
  lotes,
  estados,
  tamanho,
  aoRemover,
  aoTentarDeNovo,
}: Props) {
  return (
    <ul className="previa-etiquetas" aria-label="Etiquetas da impressão">
      {lotes.map((item) => (
        <li key={item.chave} className="previa-etiquetas__cartao">
          <Conteudo
            item={item}
            estado={estados.get(item.lote.codLot)}
            tamanho={tamanho}
            aoTentarDeNovo={aoTentarDeNovo}
          />
          <Botao
            variante="discreto"
            altura="pequena"
            aria-label={`Remover ${item.lote.codLot}`}
            onClick={() => {
              aoRemover(item.chave);
            }}
          >
            Remover
          </Botao>
        </li>
      ))}
    </ul>
  );
}

function Conteudo({
  item,
  estado,
  tamanho,
  aoTentarDeNovo,
}: {
  readonly item: LoteDaEtiqueta;
  readonly estado: EstadoDoQrDe<QrDoLote> | undefined;
  readonly tamanho: TamanhoDeEtiqueta;
  readonly aoTentarDeNovo: (codLot: string) => void;
}) {
  const { codLot } = item.lote;

  if (estado?.situacao === 'pronto') {
    return <DesenhoDaEtiquetaDeLote tamanho={tamanho} qr={estado.qr} lote={item.lote} />;
  }

  if (estado?.situacao === 'falhou') {
    return (
      <div className="previa-etiquetas__falha" role="alert">
        <p>
          <span className="previa-etiquetas__codigo">{codLot}</span> — {estado.mensagem}
        </p>
        {estado.codigo !== LOTE_NAO_CABE_NO_QR && (
          <Botao
            altura="pequena"
            onClick={() => {
              aoTentarDeNovo(codLot);
            }}
          >
            Tentar de novo
          </Botao>
        )}
      </div>
    );
  }

  return (
    <p className="previa-etiquetas__espera" role="status">
      Gerando o QR do lote <span className="previa-etiquetas__codigo">{codLot}</span>…
    </p>
  );
}
